import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../context/ToastContext";
import { AsyncButton } from "../components/AsyncButton";
import {
  getSpendBreakdown, getInsightsNarrative, getBudgets, setBudget, deleteBudget,
} from "../api";
import { CATEGORIES } from "./AIExpenseCapturePage";

const NAVY = "#0f172a";
const PRIMARY = "#1e3a8a";
const PURPLE = { bg: "#ede9fe", text: "#6d28d9" };

const primaryBtnStyle: React.CSSProperties = {
  background: PRIMARY, color: "white", border: "none",
  padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13,
};
const outlineBtnStyle: React.CSSProperties = {
  background: "white", border: "1px solid #e2e8f0", color: "#64748b",
  padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13,
};
const inputStyle: React.CSSProperties = {
  padding: "7px 9px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13,
};

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function ExpenseInsightsTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [month] = useState(currentMonth());
  const [showBudgets, setShowBudgets] = useState(false);
  // C5-05: the button only flipped this flag, and the panel it reveals renders last on the
  // page — below the total card, the category bars, the AI narrative and the budget-alert
  // box. On any realistic month it opens entirely below the fold, so clicking "Manage
  // budgets" looked like it did nothing at all.
  const budgetsRef = useRef<HTMLDivElement>(null);

  function toggleBudgets() {
    const opening = !showBudgets;
    setShowBudgets(opening);
    // Deferred a frame: the panel is conditionally rendered, so on the opening click it
    // does not exist yet at the moment this runs.
    if (opening) {
      requestAnimationFrame(() =>
        budgetsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  }
  const [budgetCategory, setBudgetCategory] = useState(CATEGORIES[0]);
  const [budgetAmount, setBudgetAmount] = useState("");

  const { data: breakdown, isLoading } = useQuery({ queryKey: ["insights-breakdown", month], queryFn: () => getSpendBreakdown(month) });
  // The narrative is now generated at most once per tenant per calendar month and cached
  // server-side, so this no longer bills the tenant's AI quota on every fresh page load —
  // it reads the same cache the module dashboard does. The client-side staleTime stays as
  // a second line of defence against refetch storms.
  const { data: narrative } = useQuery({
    queryKey: ["insights-narrative", month], queryFn: () => getInsightsNarrative(month),
    staleTime: 30 * 60 * 1000, refetchOnWindowFocus: false,
  });
  const { data: budgetsResp } = useQuery({ queryKey: ["insights-budgets"], queryFn: getBudgets, enabled: showBudgets });
  // Every figure on this tab is denominated in one currency — the primary one the backend
  // resolved — rather than the old blended total that added THB to INR (the C5-13 defect,
  // which spend-breakdown was missed by). Spend in other currencies is reported separately
  // and footnoted below, never folded in.
  const sym = breakdown?.primary_currency_symbol || "฿";
  const otherCurrencies = breakdown?.other_currencies ?? [];

  const categories = breakdown?.categories ?? [];
  const budgetAlerts = categories.filter(c => c.budget_alert);

  async function handleSetBudget() {
    const amount = parseFloat(budgetAmount);
    if (!amount || amount < 0) { toast("Enter a valid monthly budget amount", "error"); return; }
    try {
      await setBudget(budgetCategory, amount);
      qc.invalidateQueries({ queryKey: ["insights-budgets"] });
      qc.invalidateQueries({ queryKey: ["insights-breakdown"] });
      setBudgetAmount("");
      toast("Budget saved", "success");
    } catch (err: any) {
      toast(err?.response?.data?.detail || "Could not save budget", "error");
    }
  }

  async function handleDeleteBudget(category: string) {
    try {
      await deleteBudget(category);
      qc.invalidateQueries({ queryKey: ["insights-budgets"] });
      qc.invalidateQueries({ queryKey: ["insights-breakdown"] });
    } catch {
      toast("Could not remove budget", "error");
    }
  }

  if (isLoading) {
    return <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>Loading insights…</div>;
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, color: NAVY, fontSize: 18 }}>{month} at a glance</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>AI summary updated as you confirm expenses.</p>
        </div>
        <button onClick={toggleBudgets} style={outlineBtnStyle}>
          {showBudgets ? "Hide budgets" : "Manage budgets"}
        </button>
      </div>

      {/* Total spend + change */}
      <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 20px", marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>
          Total spend{breakdown?.primary_currency ? ` (${breakdown.primary_currency})` : ""}
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <span style={{ fontSize: 24, fontWeight: 700, color: NAVY }}>
            {sym}{(breakdown?.total_this_month ?? 0).toLocaleString("en", { minimumFractionDigits: 2 })}
          </span>
          {breakdown?.total_pct_change != null && (
            <span style={{ fontSize: 13, fontWeight: 600, color: breakdown.total_pct_change >= 0 ? "#dc2626" : "#16a34a" }}>
              {breakdown.total_pct_change >= 0 ? "▲" : "▼"} {Math.abs(breakdown.total_pct_change)}% vs last month
            </span>
          )}
        </div>
        {otherCurrencies.length > 0 && (
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>
            Plus {otherCurrencies.map(o => `${o.currency_symbol}${o.total.toLocaleString("en")} ${o.currency}`).join(", ")}
            {" "}in other currencies, not included above.
          </div>
        )}
      </div>

      {/* Category breakdown bars */}
      <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 14, color: NAVY }}>Spend by category</h3>
        {categories.length === 0 && <div style={{ color: "#94a3b8", fontSize: 13 }}>No confirmed expenses yet this month.</div>}
        <div style={{ display: "grid", gap: 10 }}>
          {categories.map(c => (
            <div key={c.category}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
                <span style={{ fontWeight: 600, color: NAVY }}>
                  {c.category}
                  {c.budget_alert && (
                    <span style={{ marginLeft: 8, background: "#fef9c3", color: "#854d0e", padding: "1px 8px", borderRadius: 20, fontSize: 11 }}>
                      ⚠ {c.budget_pct}% of budget
                    </span>
                  )}
                </span>
                <span style={{ color: "#64748b" }}>
                  {sym}{c.amount.toLocaleString("en", { minimumFractionDigits: 0 })} ({c.pct_of_total}%)
                  {c.pct_change_vs_last_month != null && (
                    <span style={{ marginLeft: 6, color: c.pct_change_vs_last_month >= 0 ? "#dc2626" : "#16a34a" }}>
                      {c.pct_change_vs_last_month >= 0 ? "▲" : "▼"}{Math.abs(c.pct_change_vs_last_month)}%
                    </span>
                  )}
                </span>
              </div>
              <div style={{ height: 8, background: "#f1f5f9", borderRadius: 4, overflow: "hidden" }}>
                <div style={{
                  height: "100%", width: `${Math.min(c.pct_of_total, 100)}%`,
                  background: c.budget_alert ? "#f59e0b" : PRIMARY, borderRadius: 4,
                }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AI narrative */}
      {narrative && narrative.insights.length > 0 && (
        <div style={{ display: "grid", gap: 8, marginBottom: 16 }}>
          {narrative.insights.map((text, i) => (
            <div key={i} style={{ background: PURPLE.bg, border: "1px solid #c4b5fd", borderRadius: 10, padding: "12px 16px", fontSize: 13, color: PURPLE.text }}>
              💡 {text}
            </div>
          ))}
        </div>
      )}

      {/* Budget alerts summary */}
      {budgetAlerts.length > 0 && (
        <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "12px 16px", marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: "#854d0e" }}>⚠ {budgetAlerts.length} categor{budgetAlerts.length !== 1 ? "ies" : "y"} near or over budget</div>
          {budgetAlerts.map(c => (
            <div key={c.category} style={{ fontSize: 12, color: "#854d0e", marginTop: 2 }}>
              {c.category}: {sym}{c.amount.toLocaleString("en")} of {sym}{(c.budget ?? 0).toLocaleString("en")} ({c.budget_pct}%)
            </div>
          ))}
        </div>
      )}

      {/* Budget management */}
      {showBudgets && (
        <div ref={budgetsRef} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16, scrollMarginTop: 16 }}>
          <h3 style={{ margin: "0 0 12px", fontSize: 14, color: NAVY }}>Monthly budgets by category</h3>
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
            <select value={budgetCategory} onChange={e => setBudgetCategory(e.target.value)} style={inputStyle}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="number" min={0} placeholder="Monthly amount" value={budgetAmount}
              onChange={e => setBudgetAmount(e.target.value)} style={{ ...inputStyle, width: 140 }} />
            <AsyncButton onClick={handleSetBudget} style={primaryBtnStyle}>Save</AsyncButton>
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            {(budgetsResp?.budgets ?? []).map(b => (
              <div key={b.category} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8fafc", borderRadius: 8 }}>
                <span style={{ fontSize: 13, color: NAVY }}>{b.category}</span>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: NAVY }}>{sym}{b.monthly_amount.toLocaleString("en")}</span>
                  <AsyncButton onClick={() => handleDeleteBudget(b.category)} style={{ ...outlineBtnStyle, color: "#dc2626", padding: "3px 10px", fontSize: 11 }}>Remove</AsyncButton>
                </div>
              </div>
            ))}
            {(budgetsResp?.budgets ?? []).length === 0 && <div style={{ color: "#94a3b8", fontSize: 13 }}>No budgets configured yet.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
